"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { SiteNavbar } from "@/shared/ui/site-navbar";
import { SiteFooter } from "@/shared/ui/site-footer";
import { Button } from "@/shared/ui/button";
import { HomeCtaSection, HomeFaqSection, HomeTestimonialsSection } from "@/features/home";
import type { HomePageContent } from "@/features/home/types/home-page-content";
import type { AppLocale } from "@/core/i18n/config";
import type { SearchHealerResult } from "@/features/search";
import { SERVICES_DATA } from "../../constants/services-data";
import { ServiceSituationsSection } from "../components/service-situations-section";
import { ServiceBenefitsSection } from "../components/service-benefits-section";
import { ServiceProcessSection } from "../components/service-process-section";
import { ServiceWhySection } from "../components/service-why-section";
import { ServiceCounsellorsSection } from "../components/service-counsellors-section";
interface ServiceDetailPageProps {
  content: HomePageContent;
  slug: string;
  healers: SearchHealerResult[];
  locale: AppLocale;
}
export function ServiceDetailPage(t0) {
  const $ = _c(77);
  const {
    content,
    slug,
    healers,
    locale
  } = t0;
  let t1;
  if ($[0] !== slug) {
    t1 = s => s.slug === slug;
    $[0] = slug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const service = SERVICES_DATA.find(t1);
  const pageRef = useRef(null);
  const {
    scrollY
  } = useScroll();
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [0, 800];
    t3 = [0, 240];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  const heroImageY = useTransform(scrollY, t2, t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [0, 800];
    t5 = [1, 1.15];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  const heroImageScale = useTransform(scrollY, t4, t5);
  let t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [0, 600];
    t7 = [1, 0];
    $[6] = t6;
    $[7] = t7;
  } else {
    t6 = $[6];
    t7 = $[7];
  }
  const heroOpacity = useTransform(scrollY, t6, t7);
  if (!service) {
    let t8;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="p-24 text-center">Service not found</div>;
      $[8] = t8;
    } else {
      t8 = $[8];
    }
    return t8;
  }
  const findHref = `/en/search?q=${encodeURIComponent(service.title)}&type=healers`;
  let t10;
  let t11;
  let t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 0
    };
    t9 = {
      opacity: 1
    };
    t10 = {
      opacity: 0
    };
    t11 = {
      duration: 0.5
    };
    $[9] = t10;
    $[10] = t11;
    $[11] = t8;
    $[12] = t9;
  } else {
    t10 = $[9];
    t11 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t12;
  if ($[13] !== content.navigation) {
    t12 = <div className="absolute top-0 z-50 w-full"><SiteNavbar content={content.navigation} /></div>;
    $[13] = content.navigation;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  let t14;
  let t15;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      opacity: 0
    };
    t14 = {
      opacity: 1
    };
    t15 = {
      duration: 1.2,
      ease: "easeOut"
    };
    $[15] = t13;
    $[16] = t14;
    $[17] = t15;
  } else {
    t13 = $[15];
    t14 = $[16];
    t15 = $[17];
  }
  let t16;
  if ($[18] !== heroImageScale || $[19] !== heroImageY) {
    t16 = {
      y: heroImageY,
      scale: heroImageScale
    };
    $[18] = heroImageScale;
    $[19] = heroImageY;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  let t18;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      scale: 1.15
    };
    t18 = {
      scale: 1
    };
    $[21] = t17;
    $[22] = t18;
  } else {
    t17 = $[21];
    t18 = $[22];
  }
  let t19;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      duration: 1.8,
      ease: [0.16, 1, 0.3, 1]
    };
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-transparent" />;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== heroOpacity) {
    t21 = {
      opacity: heroOpacity
    };
    $[25] = heroOpacity;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="h-10" />;
    $[27] = t22;
  } else {
    t22 = $[27];
  }
  let t23;
  let t24;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      opacity: 0,
      y: 15
    };
    t24 = {
      opacity: 1,
      y: 0
    };
    $[28] = t23;
    $[29] = t24;
  } else {
    t23 = $[28];
    t24 = $[29];
  }
  let t25;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = {
      duration: 0.8,
      delay: 0.2,
      ease: [0.16, 1, 0.3, 1]
    };
    $[30] = t25;
  } else {
    t25 = $[30];
  }
  const t26 = SERVICES_DATA.indexOf(service) + 1;
  let t27;
  if ($[31] !== t26) {
    t27 = <motion.p className="mb-3 text-[11px] font-semibold tracking-[0.3em] text-white uppercase" initial={t23} animate={t24} transition={t25}>Service {t26}</motion.p>;
    $[31] = t26;
    $[32] = t27;
  } else {
    t27 = $[32];
  }
  let t28;
  let t29;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = {
      opacity: 0,
      y: 25
    };
    t29 = {
      opacity: 1,
      y: 0
    };
    $[33] = t28;
    $[34] = t29;
  } else {
    t28 = $[33];
    t29 = $[34];
  }
  let t30;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = {
      duration: 1,
      delay: 0.3,
      ease: [0.16, 1, 0.3, 1]
    };
    $[35] = t30;
  } else {
    t30 = $[35];
  }
  let t31;
  let t32;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = {
      opacity: 0,
      y: 15
    };
    t32 = {
      opacity: 1,
      y: 0
    };
    $[36] = t31;
    $[37] = t32;
  } else {
    t31 = $[36];
    t32 = $[37];
  }
  let t33;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = {
      duration: 0.8,
      delay: 0.4,
      ease: [0.16, 1, 0.3, 1]
    };
    $[38] = t33;
  } else {
    t33 = $[38];
  }
  let t34;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Link href="/en/services" />;
    $[39] = t34;
  } else {
    t34 = $[39];
  }
  let t35;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <motion.div initial={t31} animate={t32} transition={t33}><Button size="lg" render={t34} className="group border-white/20 bg-white/5 text-white backdrop-blur-sm hover:border-white/40 [:hover,[data-pressed]]:bg-white/10"><ArrowLeft className="transition-transform duration-300 group-hover:-translate-x-0.5" />Back to Services</Button></motion.div>;
    $[40] = t35;
  } else {
    t35 = $[40];
  }
  let t36;
  let t37;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      opacity: 0,
      y: 20
    };
    t37 = {
      opacity: 1,
      y: 0
    };
    $[41] = t36;
    $[42] = t37;
  } else {
    t36 = $[41];
    t37 = $[42];
  }
  let t38;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = {
      duration: 1,
      delay: 0.5,
      ease: [0.16, 1, 0.3, 1]
    };
    $[43] = t38;
  } else {
    t38 = $[43];
  }
  let t39;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <p className="mb-2 text-[10px] font-bold tracking-[0.2em] text-white uppercase">Overview</p>;
    $[44] = t39;
  } else {
    t39 = $[44];
  }
  let t40;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <p className="mb-0.5 text-[10px] font-bold tracking-[0.2em] text-white uppercase">Format</p>;
    $[45] = t40;
  } else {
    t40 = $[45];
  }
  let t41;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <p className="mb-0.5 text-[10px] font-bold tracking-[0.2em] text-white uppercase">Duration</p>;
    $[46] = t41;
  } else {
    t41 = $[46];
  }
  const t42 = "sm:col-span-4";
  let t43;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <p className="mb-2 text-[10px] font-bold tracking-[0.2em] text-white uppercase">Recommended for</p>;
    $[47] = t43;
  } else {
    t43 = $[47];
  }
  const t44 = "columns-1 space-y-1 text-sm text-white md:columns-2";
  const t45 = service.recommendedFor.map(_temp);
  let t46;
  if ($[48] !== t45) {
    t46 = <ul className={t44}>{t45}</ul>;
    $[48] = t45;
    $[49] = t46;
  } else {
    t46 = $[49];
  }
  let t47;
  if ($[50] !== t43 || $[51] !== t46) {
    t47 = <div className={t42}>{t43}{t46}</div>;
    $[50] = t43;
    $[51] = t46;
    $[52] = t47;
  } else {
    t47 = $[52];
  }
  let t48;
  let t49;
  let t50;
  if ($[53] !== service) {
    t48 = <ServiceSituationsSection service={service} />;
    t49 = <ServiceBenefitsSection service={service} />;
    t50 = <ServiceProcessSection service={service} />;
    $[53] = service;
    $[54] = t48;
    $[55] = t49;
    $[56] = t50;
  } else {
    t48 = $[54];
    t49 = $[55];
    t50 = $[56];
  }
  let t51;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <ServiceWhySection />;
    $[57] = t51;
  } else {
    t51 = $[57];
  }
  let t52;
  if ($[58] !== healers || $[59] !== locale || $[60] !== service) {
    t52 = <ServiceCounsellorsSection service={service} healers={healers} locale={locale} />;
    $[58] = healers;
    $[59] = locale;
    $[60] = service;
    $[61] = t52;
  } else {
    t52 = $[61];
  }
  const t53 = `Ready to talk to a ${service.title} professional?`;
  const t54 = `Find a ${service.title} Professional`;
  let t55;
  if ($[62] !== content.ctaSection.imageAlts || $[63] !== findHref || $[64] !== t53 || $[65] !== t54) {
    t55 = <HomeCtaSection content={{
      title: t53,
      buttonLabel: t54,
      buttonHref: findHref,
      imageAlts: content.ctaSection.imageAlts
    }} />;
    $[62] = content.ctaSection.imageAlts;
    $[63] = findHref;
    $[64] = t53;
    $[65] = t54;
    $[66] = t55;
  } else {
    t55 = $[66];
  }
  let t56;
  if ($[67] !== content.testimonialsSection) {
    t56 = <HomeTestimonialsSection content={content.testimonialsSection} />;
    $[67] = content.testimonialsSection;
    $[68] = t56;
  } else {
    t56 = $[68];
  }
  let t57;
  if ($[69] !== content.faqSection) {
    t57 = <HomeFaqSection content={content.faqSection} />;
    $[69] = content.faqSection;
    $[70] = t57;
  } else {
    t57 = $[70];
  }
  let t58;
  if ($[71] !== content.ctaSection.imageAlts) {
    t58 = <HomeCtaSection content={{
      title: "You don't need to have it figured out yet.",
      buttonLabel: "Take a Wellness Assessment",
      buttonHref: "/en/assessment",
      imageAlts: content.ctaSection.imageAlts
    }} />;
    $[71] = content.ctaSection.imageAlts;
    $[72] = t58;
  } else {
    t58 = $[72];
  }
  let t59;
  if ($[73] !== content.footer) {
    t59 = <div className="bg-[#0b1416]"><SiteFooter content={content.footer} /></div>;
    $[73] = content.footer;
    $[74] = t59;
  } else {
    t59 = $[74];
  }
  let t60;
  if ($[75] !== findHref) {
    t60 = <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 p-3 backdrop-blur-sm sm:hidden"><Button size="lg" render={<Link href={findHref} />} className="w-full justify-center">Find a Professional</Button></div>;
    $[75] = findHref;
    $[76] = t60;
  } else {
    t60 = $[76];
  }
  return <motion.div ref={pageRef} initial={t8} animate={t9} exit={t10} transition={t11} className="relative min-h-screen bg-background pb-20 font-sans selection:bg-accent/20 selection:text-foreground sm:pb-0">{t12}<section className="relative flex min-h-[100svh] w-full flex-col justify-between overflow-hidden bg-[#122023]"><motion.div className="absolute inset-0 z-0 overflow-hidden" initial={t13} animate={t14} transition={t15}><motion.div className="absolute inset-0 will-change-transform" style={t16} initial={t17} animate={t18} transition={t19}><Image src={service.imageUrl} alt={service.title} fill={true} className="object-cover object-center brightness-100 contrast-105 grayscale-[20%]" priority={true} /></motion.div>{t20}</motion.div><motion.div className="relative z-10 flex h-full min-h-[100svh] w-full flex-col justify-between p-6 pt-32 pb-16 sm:p-10 md:p-16 lg:p-20" style={t21}>{t22}<div className="my-auto max-w-4xl">{t27}<motion.h1 className="mb-8 font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.05] font-bold tracking-tight text-white" initial={t28} animate={t29} transition={t30}>{service.title}</motion.h1>{t35}</div><motion.div className="grid grid-cols-1 gap-6 border-t border-white/10 pt-6 text-white sm:grid-cols-12 sm:gap-8 sm:pt-8" initial={t36} animate={t37} transition={t38}><div className="sm:col-span-5">{t39}<p className="text-sm leading-relaxed text-white md:text-[15px]">{service.overview}</p></div><div className="flex gap-8 sm:col-span-3 sm:flex-col sm:gap-4"><div>{t40}<p className="text-sm font-semibold text-white">{service.format}</p></div><div>{t41}<p className="text-sm font-semibold text-white">{service.duration}</p></div></div>{t47}</motion.div></motion.div></section>{t48}{t49}{t50}{t51}{t52}{t55}{t56}{t57}{t58}{t59}{t60}</motion.div>;
}
function _temp(item, i) {
  return <li key={i} className="flex items-baseline gap-2 leading-snug"><span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-white/40" />{item}</li>;
}